import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { previewCOA, updateCOA, publishCOA } from '../../services/coa';

const COAPreview = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null); // { coa, documents, translations, fees, faqs }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('COA Details');
  
  const tabs = ['COA Details', 'Support Documents',  'Translations', "FAQ's",'Licensing Highlights',];

  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    background: false,
    scoringSystem: false,
    developmentMethodology: false,
    exampleStudies: false
  });

  // FAQ collapsible state
  const [expandedFAQs, setExpandedFAQs] = useState({
    licenceGrant: false,
    usageRestrictions: false,
    intellectualProperty: false,
    feesPayments: false,
    liabilityDisclaimer: false,
    termination: false,
    governingLaw: false
  });

  useEffect(() => {
    (async () => {
      if (!id) { setError('No COA id'); setLoading(false); return; }
      setLoading(true);
      try {
        const res = await previewCOA(id);
        setData(res?.data || null);
        setError(null);
      } catch (e) {
        console.error('Failed to load preview', e);
        setError('Failed to load preview');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div className="max-w-[1200px] mx-auto p-8">Loading preview...</div>;
  if (error || !data) return <div className="max-w-[1200px] mx-auto p-8 text-red-600">{error || 'No preview data'}</div>;
  const { coa, documents = [], translations = [], faqs = [] } = data || {};
  const emptyText = 'Information not provided';
  const textOrEmpty = (value) => (
    value === undefined || value === null || value === '' ? emptyText : value
  );
  const htmlOrEmpty = (html) => {
    if (!html || html.trim() === '') return emptyText;
    return (
      <span
        className="text-sm text-gray-500 flex-1 [&_a]:text-blue-600 [&_a]:underline [&_a]:font-normal [&_a:hover]:text-blue-800"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleFAQ = (faq) => {
    setExpandedFAQs(prev => ({
      ...prev,
      [faq]: !prev[faq]
    }));
  };

  return (
    <div className="max-w-[1200px] mx-auto space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-[28px] font-semibold text-gray-900">
        Final Review before Publishing
        </h1>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/create-coa/${id}`)} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            Edit
          </button>
          <button onClick={async () => { try { await updateCOA(id, { status: 'draft' }); alert('Saved as draft'); } catch (e) { alert('Failed'); } }} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            Save as Draft
          </button>
          <button onClick={async () => { try { await publishCOA(id); alert('Published'); navigate('/coa-library'); } catch (e) { alert('Failed to publish'); } }} className="px-6 py-2.5 text-sm font-medium text-white bg-[#34D399] rounded-lg hover:bg-emerald-500 transition-colors">
            Save and Publish
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-[#F7F7F7] rounded-lg p-1 flex mb-6">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 px-4 py-3 text-sm font-medium rounded-md transition-colors ${
              activeTab === tab
                ? 'bg-[#E8F8F0] text-emerald-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'COA Details' && (
        <div className="bg-white rounded-xl shadow-sm">
          {/* COA Header with Image */}
          <div className="flex gap-6 p-6 pb-0">
            <div className="w-[104px] h-[104px] rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center flex-shrink-0 relative">
              <div className="absolute inset-0 flex items-center justify-center">
                {/* <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                  <path d="M32 8C18.7 8 8 18.7 8 32C8 38.3 10.5 44 14.5 48.3" stroke="white" strokeWidth="6" strokeLinecap="round"/>
                  <path d="M32 8C45.3 8 56 18.7 56 32" stroke="white" strokeWidth="6" strokeLinecap="round"/>
                  <polygon points="48,48 56,40 56,56" fill="white"/>
                </svg> */}
              </div>
              {coa?.logoUrl ? (
                <img src={coa.logoUrl} alt="COA Logo" className="w-full h-full object-cover rounded-lg" />
              ) : (
                <div className="absolute bottom-1 left-1 bg-[#3B9FE8] text-white text-xs font-bold px-2.5 py-1 rounded">{coa?.code || ''}</div>
              )}
            </div>
            <div className="flex-1 flex items-start justify-between pt-2">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-1.5 leading-tight">{coa?.name || ''}</h2>
                <p className="text-sm text-gray-500">{textOrEmpty(coa?.diseaseOrCondition)}</p>
              </div>
              <div className="text-right text-xs text-gray-400 leading-relaxed">
                Last Updated - {coa?.updatedAt ? new Date(coa.updatedAt).toLocaleDateString('en-GB') : emptyText}
              </div>
            </div>
          </div>

          {/* Description Section */}
          <div className="px-6 pt-8 pb-6">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Description</h3>
            <div 
              className="prose max-w-none text-sm text-gray-600 leading-relaxed [&_a]:text-blue-600 [&_a]:underline [&_a]:font-normal [&_a:hover]:text-blue-800" 
              dangerouslySetInnerHTML={{ __html: coa?.description || '' }} 
            />
          </div>

          {/* Copyright & Usage Rights Section */}
          <div className="px-6 pb-8 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-900 mb-6">Copyright & Usage Rights</h3>
            <div className="space-y-0">
              <div className="flex py-4 border-b border-gray-100">
                <span className="text-sm text-gray-700 w-72 flex-shrink-0">Authors</span>
                <span className="text-sm text-gray-500 flex-1">
                  {Array.isArray(coa?.authors)
                    ? textOrEmpty(coa.authors.join(', '))
                    : textOrEmpty(coa?.authors)}
                </span>
              </div>
              <div className="flex py-4 border-b border-gray-100">
                <span className="text-sm text-gray-700 w-72 flex-shrink-0">© Owner / full copyright message</span>
                <span className="text-sm text-gray-500 flex-1">{textOrEmpty(coa?.copyright)}</span>
              </div>
              <div className="flex py-4 border-b border-gray-100">
                <span className="text-sm text-gray-700 w-72 flex-shrink-0">Permissions to use / Licence</span>
                {htmlOrEmpty(coa?.licensePermissions)}
              </div>
              <div className="flex py-4">
                <span className="text-sm text-gray-700 w-72 flex-shrink-0">Restrictions</span>
                {htmlOrEmpty(coa?.restrictions)}
              </div>
            </div>
          </div>

          {/* Details Section */}
          <div className="px-6 py-8">
            <h3 className="text-base font-semibold text-gray-900 mb-6">Details</h3>
            <div className="space-y-0">
              <div className="flex py-4 border-b border-gray-100">
                <span className="text-sm text-gray-700 w-72 flex-shrink-0">Type of COA</span>
                <span className="text-sm text-gray-500 flex-1">{textOrEmpty(coa?.details?.typeOfCOA)}</span>
              </div>
              <div className="flex py-4 border-b border-gray-100">
                <span className="text-sm text-gray-700 w-72 flex-shrink-0">Target Age group</span>
                <span className="text-sm text-gray-500 flex-1">{textOrEmpty(coa?.details?.targetAgeGroup)}</span>
              </div>
              <div className="flex py-4 border-b border-gray-100">
                <span className="text-sm text-gray-700 w-72 flex-shrink-0">Respondents</span>
                <span className="text-sm text-gray-500 flex-1">{textOrEmpty(coa?.details?.respondents)}</span>
              </div>
              <div className="flex py-4 border-b border-gray-100">
                <span className="text-sm text-gray-700 w-72 flex-shrink-0">Gender</span>
                <span className="text-sm text-gray-500 flex-1">{textOrEmpty(coa?.details?.gender)}</span>
              </div>
              <div className="flex py-4 border-b border-gray-100">
                <span className="text-sm text-gray-700 w-72 flex-shrink-0">Number of Items</span>
                <span className="text-sm text-gray-500 flex-1">{textOrEmpty(coa?.details?.numItems)}</span>
              </div>
              <div className="flex py-4 border-b border-gray-100">
                <span className="text-sm text-gray-700 w-72 flex-shrink-0">Recall period</span>
                <span className="text-sm text-gray-500 flex-1">{textOrEmpty(coa?.details?.recallPeriod)}</span>
              </div>
              <div className="flex py-4 border-b border-gray-100">
                <span className="text-sm text-gray-700 w-72 flex-shrink-0">Response scales</span>
                <span className="text-sm text-gray-500 flex-1">{textOrEmpty(coa?.details?.responseScales)}</span>
              </div>
              <div className="flex py-4 border-b border-gray-100">
                <span className="text-sm text-gray-700 w-72 flex-shrink-0">Domains / concepts measured</span>
                <span className="text-sm text-gray-500 flex-1">{textOrEmpty(coa?.details?.domains)}</span>
              </div>
              <div className="flex py-4 border-b border-gray-100">
                <span className="text-sm text-gray-700 w-72 flex-shrink-0">Administration time</span>
                <span className="text-sm text-gray-500 flex-1">{textOrEmpty(coa?.details?.adminTime)}</span>
              </div>
              <div className="flex py-4 border-b border-gray-100">
                <span className="text-sm text-gray-700 w-72 flex-shrink-0">Administration Modes</span>
                <span className="text-sm text-gray-500 flex-1">{textOrEmpty(coa?.details?.adminModes)}</span>
              </div>
              <div className="flex py-4 border-b border-gray-100">
                <span className="text-sm text-gray-700 w-72 flex-shrink-0">Notes for eCOA reproductions</span>
                <span className="text-sm text-gray-500 flex-1 [&_a]:text-blue-600 [&_a]:underline [&_a]:font-normal [&_a:hover]:text-blue-800" dangerouslySetInnerHTML={{ __html: coa?.ecoaNotes || '' }} />
              </div>
              <div className="flex py-4">
                <span className="text-sm text-gray-700 w-72 flex-shrink-0">Additional details</span>
                <span className="text-sm text-gray-500 flex-1 [&_a]:text-blue-600 [&_a]:underline [&_a]:font-normal [&_a:hover]:text-blue-800" dangerouslySetInnerHTML={{ __html: coa?.otherInformation || '' }} />
              </div>
            </div>
          </div>

          {/* References & External Resources Section */}
          <div className="px-6 py-8 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-900 mb-6">References & External Resources</h3>
            <div className="space-y-0">
              <div className="flex py-4 border-b border-gray-100">
                <span className="text-sm text-gray-700 w-72 flex-shrink-0">Key references</span>
                <span className="text-sm text-gray-500 flex-1 [&_a]:text-blue-600 [&_a]:underline [&_a]:font-normal [&_a:hover]:text-blue-800">
                  {coa?.references ? <span dangerouslySetInnerHTML={{ __html: coa.references }} /> : emptyText}
                </span>
              </div>
              <div className="flex py-4">
                <span className="text-sm text-gray-700 w-72 flex-shrink-0">Useful external links</span>
                <span className="text-sm text-gray-500 flex-1">
                  {coa?.externalLinks ? (
                    (() => {
                      // Split by newlines or commas to handle multiple links
                      const links = coa.externalLinks.split(/\n|,/).map(l => l.trim()).filter(l => l);
                      if (links.length > 1) {
                        return (
                          <ul className="list-disc list-inside">
                            {links.map((l, i) => (
                              <li key={i}>
                                <a className="text-emerald-600 underline" href={l} target="_blank" rel="noreferrer">{l}</a>
                              </li>
                            ))}
                          </ul>
                        );
                      } else if (links.length === 1) {
                        return (
                          <a className="text-emerald-600 underline" href={links[0]} target="_blank" rel="noreferrer">{links[0]}</a>
                        );
                      }
                      return coa.externalLinks;
                    })()
                  ) : emptyText}
                </span>
              </div>
            </div>
          </div>

          {/* Collapsible Sections */}
          <div className="px-6 pt-6 pb-8 space-y-4">
            {/* Background */}
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <button
                onClick={() => toggleSection('background')}
                className="w-full flex items-center justify-between px-6 py-5 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded flex items-center justify-center bg-[#E8F8F0]">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <rect x="3" y="3" width="10" height="10" rx="1.5" stroke="#10B981" strokeWidth="1.5" fill="none"/>
                      <path d="M5 7L7 9L11 5" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className="text-base font-medium text-gray-700">Background</span>
                </div>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className={`transform transition-transform ${expandedSections.background ? 'rotate-180' : ''}`}
                >
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {expandedSections.background && (
                <div className="px-6 py-5 border-t border-gray-200 bg-white">
                  <div className="prose max-w-none text-sm text-gray-600 leading-relaxed [&_a]:text-blue-600 [&_a]:underline [&_a]:font-normal [&_a:hover]:text-blue-800" dangerouslySetInnerHTML={{ __html: coa?.background || 'No background information available.' }} />
                </div>
              )}
            </div>

            {/* Scoring system */}
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <button
                onClick={() => toggleSection('scoringSystem')}
                className="w-full flex items-center justify-between px-6 py-5 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded flex items-center justify-center bg-[#E8F8F0]">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="8" r="5" stroke="#10B981" strokeWidth="1.5" fill="none"/>
                    </svg>
                  </div>
                  <span className="text-base font-medium text-gray-700">Scoring system</span>
                </div>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className={`transform transition-transform ${expandedSections.scoringSystem ? 'rotate-180' : ''}`}
                >
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {expandedSections.scoringSystem && (
                <div className="px-6 py-5 border-t border-gray-200 bg-white">
                  <div className="prose max-w-none text-sm text-gray-600 leading-relaxed [&_a]:text-blue-600 [&_a]:underline [&_a]:font-normal [&_a:hover]:text-blue-800" dangerouslySetInnerHTML={{ __html: coa?.scoringSystem || 'No scoring system information available.' }} />
                </div>
              )}
            </div>

            {/* Development Methodology */}
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <button
                onClick={() => toggleSection('developmentMethodology')}
                className="w-full flex items-center justify-between px-6 py-5 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded flex items-center justify-center bg-[#E8F8F0]">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 3L8 8L11 11" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className="text-base font-medium text-gray-700">Development Methodology</span>
                </div>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className={`transform transition-transform ${expandedSections.developmentMethodology ? 'rotate-180' : ''}`}
                >
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {expandedSections.developmentMethodology && (
                <div className="px-6 py-5 border-t border-gray-200 bg-white">
                  <div className="prose max-w-none text-sm text-gray-600 leading-relaxed [&_a]:text-blue-600 [&_a]:underline [&_a]:font-normal [&_a:hover]:text-blue-800" dangerouslySetInnerHTML={{ __html: coa?.developmentMethodology || 'No development methodology information available.' }} />
                </div>
              )}
            </div>

            {/* Example studies */}
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
              <button
                onClick={() => toggleSection('exampleStudies')}
                className="w-full flex items-center justify-between px-6 py-5 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded flex items-center justify-center bg-[#E8F8F0]">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <rect x="4" y="2.5" width="8" height="11" rx="1" stroke="#10B981" strokeWidth="1.5" fill="none"/>
                      <line x1="6" y1="5.5" x2="10" y2="5.5" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round"/>
                      <line x1="6" y1="8" x2="10" y2="8" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round"/>
                      <line x1="6" y1="10.5" x2="8" y2="10.5" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <span className="text-base font-medium text-gray-700">Example studies</span>
                </div>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className={`transform transition-transform ${expandedSections.exampleStudies ? 'rotate-180' : ''}`}
                >
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {expandedSections.exampleStudies && (
                <div className="px-6 py-5 border-t border-gray-200 bg-white">
                  <div className="prose max-w-none text-sm text-gray-600 leading-relaxed [&_a]:text-blue-600 [&_a]:underline [&_a]:font-normal [&_a:hover]:text-blue-800" dangerouslySetInnerHTML={{ __html: coa?.exampleStudies || 'No example studies information available.' }} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'Support Documents' && (
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Support Documents</h2>
            
            {/* Documents Grid */}

<div className="space-y-3">
  {documents.length ? (
    documents.map(doc => (

        <div className="flex items-center justify-between px-5 py-4 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 flex-shrink-0">
            <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
              <rect x="9" y="5" width="26" height="34" rx="2.5" fill="#A7F3D0"/>
              <rect x="13" y="11" width="18" height="1.5" rx="0.75" fill="#10B981"/>
              <rect x="13" y="16" width="18" height="1.5" rx="0.75" fill="#10B981"/>
              <rect x="13" y="21" width="14" height="1.5" rx="0.75" fill="#10B981"/>
              <rect x="13" y="26" width="11" height="1.5" rx="0.75" fill="#10B981"/>
            </svg>
          </div>
          <div>
            <h3 className="text-base font-medium text-gray-900 mb-0.5">{doc.name}</h3>
            <p className="text-sm text-gray-400">{doc.detail}</p>
          </div>
        </div>

        {doc.fileUrl ? (
  <button className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
  Download
</button>        ) : (
          <span className="text-sm text-gray-400">No file</span>
        )}
      
      </div>




    //   <div key={doc._id} className="flex items-center justify-between px-5 py-4 bg-white border border-gray-200 rounded-xl">
    //     <div>
    //       <h3 className="text-base font-medium text-gray-900 mb-0.5">{doc.name}</h3>
    //       <p className="text-sm text-gray-400">{doc.detail}</p>
    //     </div>
    //     {doc.fileUrl ? (
    //       <a href={doc.fileUrl} target="_blank" rel="noreferrer" className="text-sm font-medium text-emerald-600">View</a>
    //     ) : (
    //       <span className="text-sm text-gray-400">No file</span>
    //     )}
    //   </div>
    ))
  ) : (
    <p className="text-gray-500">No documents.</p>
  )}
</div>
            <div className="grid grid-cols-2 gap-5">
              {/* Document 1 - Green Lines Icon */}


              {/* <div className="flex items-center justify-between px-5 py-4 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 flex-shrink-0">
                    <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                      <rect x="9" y="5" width="26" height="34" rx="2.5" fill="#A7F3D0"/>
                      <rect x="13" y="11" width="18" height="1.5" rx="0.75" fill="#10B981"/>
                      <rect x="13" y="16" width="18" height="1.5" rx="0.75" fill="#10B981"/>
                      <rect x="13" y="21" width="14" height="1.5" rx="0.75" fill="#10B981"/>
                      <rect x="13" y="26" width="11" height="1.5" rx="0.75" fill="#10B981"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-gray-900 mb-0.5">Scoring Guide</h3>
                    <p className="text-sm text-gray-400">Lorem ipsum dolor sit amet, consectetur</p>
                  </div>
                </div>
                <button className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
                  Download
                </button>
              </div> */}


              {/* Document 2 - Green Folder Icon */}
              {/* <div className="flex items-center justify-between px-5 py-4 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 flex-shrink-0">
                    <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                      <path d="M7 15C7 13.3431 8.34315 12 10 12H18L21 16H35C36.6569 16 38 17.3431 38 19V32C38 33.6569 36.6569 35 35 35H10C8.34315 35 7 33.6569 7 32V15Z" fill="#86EFAC"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-gray-900 mb-0.5">Scoring Guide</h3>
                    <p className="text-sm text-gray-400">Lorem ipsum dolor sit amet, consectetur</p>
                  </div>
                </div>
                <button className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
                  Download
                </button>
              </div> */}

              {/* Document 3 - Green Lines Icon */}
              {/* <div className="flex items-center justify-between px-5 py-4 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 flex-shrink-0">
                    <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                      <rect x="9" y="5" width="26" height="34" rx="2.5" fill="#A7F3D0"/>
                      <rect x="13" y="11" width="18" height="1.5" rx="0.75" fill="#10B981"/>
                      <rect x="13" y="16" width="18" height="1.5" rx="0.75" fill="#10B981"/>
                      <rect x="13" y="21" width="14" height="1.5" rx="0.75" fill="#10B981"/>
                      <rect x="13" y="26" width="11" height="1.5" rx="0.75" fill="#10B981"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-gray-900 mb-0.5">Scoring Guide</h3>
                    <p className="text-sm text-gray-400">Lorem ipsum dolor sit amet, consectetur</p>
                  </div>
                </div>
                <button className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
                  Download
                </button>
              </div> */}

              {/* Document 4 - Green Folder Icon */}
              {/* <div className="flex items-center justify-between px-5 py-4 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 flex-shrink-0">
                    <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                      <path d="M7 15C7 13.3431 8.34315 12 10 12H18L21 16H35C36.6569 16 38 17.3431 38 19V32C38 33.6569 36.6569 35 35 35H10C8.34315 35 7 33.6569 7 32V15Z" fill="#86EFAC"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-gray-900 mb-0.5">Scoring Guide</h3>
                    <p className="text-sm text-gray-400">Lorem ipsum dolor sit amet, consectetur</p>
                  </div>
                </div>
                <button className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
                  Download
                </button>
              </div> */}

              {/* Document 5 - Green Lines Icon */}
              {/* <div className="flex items-center justify-between px-5 py-4 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 flex-shrink-0">
                    <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                      <rect x="9" y="5" width="26" height="34" rx="2.5" fill="#A7F3D0"/>
                      <rect x="13" y="11" width="18" height="1.5" rx="0.75" fill="#10B981"/>
                      <rect x="13" y="16" width="18" height="1.5" rx="0.75" fill="#10B981"/>
                      <rect x="13" y="21" width="14" height="1.5" rx="0.75" fill="#10B981"/>
                      <rect x="13" y="26" width="11" height="1.5" rx="0.75" fill="#10B981"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-gray-900 mb-0.5">Scoring Guide</h3>
                    <p className="text-sm text-gray-400">Lorem ipsum dolor sit amet, consectetur</p>
                  </div>
                </div>
                <button className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
                  Download
                </button>
              </div> */}

              {/* Document 6 - Green Folder Icon */}
              {/* <div className="flex items-center justify-between px-5 py-4 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 flex-shrink-0">
                    <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                      <path d="M7 15C7 13.3431 8.34315 12 10 12H18L21 16H35C36.6569 16 38 17.3431 38 19V32C38 33.6569 36.6569 35 35 35H10C8.34315 35 7 33.6569 7 32V15Z" fill="#86EFAC"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-gray-900 mb-0.5">Scoring Guide</h3>
                    <p className="text-sm text-gray-400">Lorem ipsum dolor sit amet, consectetur</p>
                  </div>
                </div>
                <button className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
                  Download
                </button>
              </div> */}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'Licensing Highlights' && (
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-6 pb-8">
            {/* Header with Date */}
            <div className="flex items-start justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Terms & Conditions – Licence Agreement</h2>
              <p className="text-sm text-gray-600">{coa.createdAt ? new Date(coa.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : ''}</p>
            </div>
            
            {/* Content Sections */}
            <div className="space-y-6">
            {htmlOrEmpty(coa?.permissionsToUse)}

              {/* 1. Licence Grant */}
                        </div>
          </div>
        </div>
      )}


      {activeTab === "FAQ's" && (
        <div className="space-y-6">

<div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">FAQ's</h2>
           
            </div>
        {[...(faqs || [])].sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0)).map((faq) => (
          <div key={faq._id || faq.id} className="bg-white rounded-xl border border-gray-200 overflow-visible">
            <div className="flex items-start justify-between px-6 py-5 gap-4">
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900 mb-3">{faq.question}</h3>
                {faq.isList ? (
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-2">
                    {faq.answer.split('\n').map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                )}
              </div>
              {/* <ActionMenu 
                items={faqMenuItems}
                onAction={handleFAQAction}
                itemId={faq.id}
              /> */}
            </div>
          </div>
        ))}
      </div>
      )}

      {activeTab === "Translations" && (
         <div className="bg-white rounded-xl shadow-sm">
         <div className="p-6">
           <h2 className="text-xl font-semibold text-gray-900 mb-6">Translations</h2>
           
           {/* Documents Grid */}

<div className="space-y-3">
 {translations.length ? (
   translations.map(t => {
     const status = (t.adminStatus || t.status || '').toString();
     const statusLabel = status.replace(/[_-]+/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
     const statusClass = status === 'approved' || status === 'certified' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-100 text-gray-700 border border-gray-200';
     const dateStr = t.updatedAt ? new Date(t.updatedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }) : '';
     return (
      <div key={t._id} className="flex items-center justify-between px-6 py-6 bg-white border border-gray-200 rounded-xl">
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-11 h-11 flex-shrink-0 rounded-lg bg-emerald-50 flex items-center justify-center">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <rect x="5" y="3" width="14" height="18" rx="2" fill="#A7F3D0"/>
              <rect x="8" y="7" width="8" height="1.5" rx="0.75" fill="#10B981"/>
              <rect x="8" y="10" width="8" height="1.5" rx="0.75" fill="#10B981"/>
              <rect x="8" y="13" width="6" height="1.5" rx="0.75" fill="#10B981"/>
            </svg>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-medium text-gray-900 truncate">{t.country} - {t.language}</h3>
              {status && (
                <span className={`inline-flex items-center justify-center w-[122px] whitespace-nowrap px-2 py-0.5 text-xs font-semibold rounded-full ${statusClass}`}>{statusLabel}</span>
              )}
              {t.certified ? (
                <span className="inline-flex items-center justify-center w-[104px] whitespace-nowrap px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-50 text-blue-800 border border-blue-200">Certified</span>
              ) : (
                <span className="inline-flex items-center justify-center w-[104px] whitespace-nowrap px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600 border border-gray-200">Non Certified</span>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-1 flex items-center gap-4">
              {/* Size not available from API; keeping placeholder dash */}
              <span>{emptyText}</span>
              {dateStr && <span>{dateStr}</span>}
            </div>
          </div>
        </div>
        <div>
          {t.translationFileUrl ? (
            <a href={t.translationFileUrl} target="_blank" rel="noreferrer" className="w-9 h-9 inline-flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:text-gray-900">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M8 11l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M4 21h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </a>
          ) : (
            <span className="text-sm text-gray-400">No file</span>
          )}
        </div>
      </div>
     );
   })
 ) : (
   <p className="text-gray-500">No translations.</p>
 )}
</div>
           <div className="grid grid-cols-2 gap-5">
             {/* Document 1 - Green Lines Icon */}


             {/* <div className="flex items-center justify-between px-5 py-4 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors">
               <div className="flex items-center gap-4">
                 <div className="w-11 h-11 flex-shrink-0">
                   <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                     <rect x="9" y="5" width="26" height="34" rx="2.5" fill="#A7F3D0"/>
                     <rect x="13" y="11" width="18" height="1.5" rx="0.75" fill="#10B981"/>
                     <rect x="13" y="16" width="18" height="1.5" rx="0.75" fill="#10B981"/>
                     <rect x="13" y="21" width="14" height="1.5" rx="0.75" fill="#10B981"/>
                     <rect x="13" y="26" width="11" height="1.5" rx="0.75" fill="#10B981"/>
                   </svg>
                 </div>
                 <div>
                   <h3 className="text-base font-medium text-gray-900 mb-0.5">Scoring Guide</h3>
                   <p className="text-sm text-gray-400">Lorem ipsum dolor sit amet, consectetur</p>
                 </div>
               </div>
               <button className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
                 Download
               </button>
             </div> */}


             {/* Document 2 - Green Folder Icon */}
             {/* <div className="flex items-center justify-between px-5 py-4 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors">
               <div className="flex items-center gap-4">
                 <div className="w-11 h-11 flex-shrink-0">
                   <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                     <path d="M7 15C7 13.3431 8.34315 12 10 12H18L21 16H35C36.6569 16 38 17.3431 38 19V32C38 33.6569 36.6569 35 35 35H10C8.34315 35 7 33.6569 7 32V15Z" fill="#86EFAC"/>
                   </svg>
                 </div>
                 <div>
                   <h3 className="text-base font-medium text-gray-900 mb-0.5">Scoring Guide</h3>
                   <p className="text-sm text-gray-400">Lorem ipsum dolor sit amet, consectetur</p>
                 </div>
               </div>
               <button className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
                 Download
               </button>
             </div> */}

             {/* Document 3 - Green Lines Icon */}
             {/* <div className="flex items-center justify-between px-5 py-4 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors">
               <div className="flex items-center gap-4">
                 <div className="w-11 h-11 flex-shrink-0">
                   <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                     <rect x="9" y="5" width="26" height="34" rx="2.5" fill="#A7F3D0"/>
                     <rect x="13" y="11" width="18" height="1.5" rx="0.75" fill="#10B981"/>
                     <rect x="13" y="16" width="18" height="1.5" rx="0.75" fill="#10B981"/>
                     <rect x="13" y="21" width="14" height="1.5" rx="0.75" fill="#10B981"/>
                     <rect x="13" y="26" width="11" height="1.5" rx="0.75" fill="#10B981"/>
                   </svg>
                 </div>
                 <div>
                   <h3 className="text-base font-medium text-gray-900 mb-0.5">Scoring Guide</h3>
                   <p className="text-sm text-gray-400">Lorem ipsum dolor sit amet, consectetur</p>
                 </div>
               </div>
               <button className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
                 Download
               </button>
             </div> */}

             {/* Document 4 - Green Folder Icon */}
             {/* <div className="flex items-center justify-between px-5 py-4 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors">
               <div className="flex items-center gap-4">
                 <div className="w-11 h-11 flex-shrink-0">
                   <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                     <path d="M7 15C7 13.3431 8.34315 12 10 12H18L21 16H35C36.6569 16 38 17.3431 38 19V32C38 33.6569 36.6569 35 35 35H10C8.34315 35 7 33.6569 7 32V15Z" fill="#86EFAC"/>
                   </svg>
                 </div>
                 <div>
                   <h3 className="text-base font-medium text-gray-900 mb-0.5">Scoring Guide</h3>
                   <p className="text-sm text-gray-400">Lorem ipsum dolor sit amet, consectetur</p>
                 </div>
               </div>
               <button className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
                 Download
               </button>
             </div> */}

             {/* Document 5 - Green Lines Icon */}
             {/* <div className="flex items-center justify-between px-5 py-4 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors">
               <div className="flex items-center gap-4">
                 <div className="w-11 h-11 flex-shrink-0">
                   <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                     <rect x="9" y="5" width="26" height="34" rx="2.5" fill="#A7F3D0"/>
                     <rect x="13" y="11" width="18" height="1.5" rx="0.75" fill="#10B981"/>
                     <rect x="13" y="16" width="18" height="1.5" rx="0.75" fill="#10B981"/>
                     <rect x="13" y="21" width="14" height="1.5" rx="0.75" fill="#10B981"/>
                     <rect x="13" y="26" width="11" height="1.5" rx="0.75" fill="#10B981"/>
                   </svg>
                 </div>
                 <div>
                   <h3 className="text-base font-medium text-gray-900 mb-0.5">Scoring Guide</h3>
                   <p className="text-sm text-gray-400">Lorem ipsum dolor sit amet, consectetur</p>
                 </div>
               </div>
               <button className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
                 Download
               </button>
             </div> */}

             {/* Document 6 - Green Folder Icon */}
             {/* <div className="flex items-center justify-between px-5 py-4 bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-colors">
               <div className="flex items-center gap-4">
                 <div className="w-11 h-11 flex-shrink-0">
                   <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                     <path d="M7 15C7 13.3431 8.34315 12 10 12H18L21 16H35C36.6569 16 38 17.3431 38 19V32C38 33.6569 36.6569 35 35 35H10C8.34315 35 7 33.6569 7 32V15Z" fill="#86EFAC"/>
                   </svg>
                 </div>
                 <div>
                   <h3 className="text-base font-medium text-gray-900 mb-0.5">Scoring Guide</h3>
                   <p className="text-sm text-gray-400">Lorem ipsum dolor sit amet, consectetur</p>
                 </div>
               </div>
               <button className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
                 Download
               </button>
             </div> */}
           </div>
         </div>
       </div>
      )}
    </div>
  );
};

export default COAPreview;


